import { events } from '@/app/events/events';
import { closeRightSidePanels, registerRightSidePanelCloser } from '@/app/helpers/closeRightSidePanels';
import { focusGrid } from '@/app/helpers/focusGrid.js';
import type { SearchOptions } from '@/app/quadratic-core-types';
import type { TransactionInfo } from '@/app/shared/types/transactionInfo';
import type { User } from '@/auth/auth';
import { atom, getDefaultStore } from 'jotai';
import type { FilePermission, TeamSettings } from 'quadratic-shared/typesAndSchemas';
import type { ConnectionType } from 'quadratic-shared/typesAndSchemasConnections';
import type { SetStateAction } from 'react';

export type ConnectionsMenuOverride = {
  onConnectionReady: (conn: { uuid: string; type: ConnectionType; name: string }) => void;
  confirmOnExit: boolean;
  actionLabel: string;
};

export const connectionsMenuOverrideAtom = atom<ConnectionsMenuOverride | null>(null);

type AnnotationState = 'dropdown' | 'date-format' | 'calendar' | 'calendar-time';

/** Initial state for the find & replace panel */
export interface FindReplaceSeed {
  searchOptions?: SearchOptions;
  /** when false, the panel opens with the replace section collapsed (find-only) */
  replace?: boolean;
}

export type ConnectionsMenuState =
  | undefined
  | { connectionType: ConnectionType }
  | { connectionUuid: string; connectionType: ConnectionType };

export interface EditorInteractionState {
  isRunningAsyncAction: boolean;
  transactionsInfo: TransactionInfo[];
  showCellTypeMenu: boolean | 'connections';
  showCommandPalette: boolean;
  showAddConnectionMenu: boolean;
  showConnectionsMenu: ConnectionsMenuState;
  showGoToMenu: boolean;
  showCellEditHistory: boolean;
  showFeedbackMenu: boolean;
  showRenameFileMenu: boolean;
  showShareFileMenu: boolean;
  showExportFileMenu: boolean;
  /** PDF options dialog; the mode selects whether it downloads or prints. */
  showPdfExportMenu: false | 'export' | 'print';
  showGoogleSheetsImport: boolean;
  /** Find & replace side panel; may carry a seed (search options / replace collapsed) */
  showFindReplace: boolean | FindReplaceSeed;
  showContextMenu: boolean;
  showValidation: boolean | string;
  showConditionalFormat: boolean | string;
  /** Comments side panel listing all threads in the file */
  showComments: boolean;
  /** false = closed; 'new' = create form; '<uuid>' = edit that chart */
  showChartPanel: boolean | string;
  /** false = closed; true = list; 'A1' = edit the pivot table anchored there */
  showPivotPanel: boolean | string;
  /** Create PivotTable dialog */
  showCreatePivotTable: boolean;
  /** Name Manager dialog for defined names */
  showDefinedNames: boolean;
  showLogs: boolean;
  annotationState?: AnnotationState;
  permissions: FilePermission[];
  settings: TeamSettings;
  user?: User;
  fileUuid: string;
  teamUuid: string;
  canManageBilling: boolean;
  canManageAIOverage: boolean;
  connectionUuid: string;
  follow?: string;
  undo: boolean;
  redo: boolean;
}

export const defaultEditorInteractionState: EditorInteractionState = {
  isRunningAsyncAction: false,
  transactionsInfo: [],
  showCellTypeMenu: false,
  showCommandPalette: false,
  showAddConnectionMenu: false,
  showConnectionsMenu: undefined,
  showGoToMenu: false,
  showCellEditHistory: false,
  showFeedbackMenu: false,
  showRenameFileMenu: false,
  showShareFileMenu: false,
  showExportFileMenu: false,
  showPdfExportMenu: false,
  showGoogleSheetsImport: false,
  showFindReplace: false,
  showContextMenu: false,
  showValidation: false,
  showConditionalFormat: false,
  showComments: false,
  showChartPanel: false,
  showPivotPanel: false,
  showCreatePivotTable: false,
  showDefinedNames: false,
  showLogs: false,
  annotationState: undefined,
  permissions: [],
  settings: {
    analyticsAi: false,
  },
  user: undefined,
  fileUuid: '',
  teamUuid: '',
  canManageBilling: false,
  canManageAIOverage: false,
  connectionUuid: '',
  follow: undefined,
  undo: false,
  redo: false,
};

const editorInteractionStateBaseAtom = atom<EditorInteractionState>(defaultEditorInteractionState);

const isModalShowing = (state: EditorInteractionState): boolean => {
  return !!(
    state.showCellTypeMenu ||
    state.showCommandPalette ||
    state.showAddConnectionMenu ||
    state.showConnectionsMenu ||
    state.showGoToMenu ||
    state.showFeedbackMenu ||
    state.showRenameFileMenu ||
    state.showShareFileMenu ||
    state.showExportFileMenu ||
    state.showPdfExportMenu ||
    state.showGoogleSheetsImport ||
    state.showContextMenu
  );
};

export const editorInteractionStateAtom = atom(
  (get) => get(editorInteractionStateBaseAtom),
  (get, set, update: SetStateAction<EditorInteractionState>) => {
    const oldValue = get(editorInteractionStateBaseAtom);
    const newValue = typeof update === 'function' ? update(oldValue) : update;

    const oldModalShow = isModalShowing(oldValue);
    const newModalShow = isModalShowing(newValue);

    if (oldModalShow && !newModalShow) {
      focusGrid();
    }

    // Right-side panel exclusivity: when one of the panels owned by this atom
    // transitions from closed to open, close the sibling panels in the same
    // write and close panels owned by other atoms via the registry.
    const openingCellEditHistory = oldValue.showCellEditHistory === false && newValue.showCellEditHistory !== false;
    const openingValidation = oldValue.showValidation === false && newValue.showValidation !== false;
    const openingConditionalFormat =
      oldValue.showConditionalFormat === false && newValue.showConditionalFormat !== false;
    const openingFindReplace = oldValue.showFindReplace === false && newValue.showFindReplace !== false;

    let finalValue = newValue;
    if (openingCellEditHistory || openingValidation || openingConditionalFormat || openingFindReplace) {
      finalValue = {
        ...newValue,
        showCellEditHistory: openingCellEditHistory ? newValue.showCellEditHistory : false,
        showValidation: openingValidation ? newValue.showValidation : false,
        showConditionalFormat: openingConditionalFormat ? newValue.showConditionalFormat : false,
        showFindReplace: openingFindReplace ? newValue.showFindReplace : false,
      };
      closeRightSidePanels(['cellEditHistory', 'validation', 'conditionalFormat', 'findReplace']);
    }

    set(editorInteractionStateBaseAtom, finalValue);
  }
);

// Closes the right-side panels owned by this atom. Registered with the
// exclusivity registry so setters of other panel atoms (code editor, scheduled
// tasks, file history) can close them. Writes to the base atom to avoid
// re-entering the exclusivity logic in the setter above.
const closeEditorInteractionStatePanels = () => {
  const store = getDefaultStore();
  const prev = store.get(editorInteractionStateBaseAtom);
  if (
    prev.showCellEditHistory === false &&
    prev.showValidation === false &&
    prev.showConditionalFormat === false &&
    prev.showFindReplace === false
  ) {
    return;
  }
  store.set(editorInteractionStateBaseAtom, {
    ...prev,
    showCellEditHistory: false,
    showValidation: false,
    showConditionalFormat: false,
    showFindReplace: false,
  });
};
registerRightSidePanelCloser('cellEditHistory', closeEditorInteractionStatePanels);
registerRightSidePanelCloser('validation', closeEditorInteractionStatePanels);
registerRightSidePanelCloser('conditionalFormat', closeEditorInteractionStatePanels);
registerRightSidePanelCloser('findReplace', closeEditorInteractionStatePanels);

let editorInteractionStateListenersInitialized = false;

export const initEditorInteractionStateListeners = () => {
  if (editorInteractionStateListenersInitialized) return () => {};
  editorInteractionStateListenersInitialized = true;

  const store = getDefaultStore();

  const handleTransaction = (transaction: TransactionInfo, add: boolean) => {
    store.set(editorInteractionStateAtom, (prev) => {
      const newTransactionsInfo = [
        ...prev.transactionsInfo.filter((t) => t.transactionId !== transaction.transactionId),
        ...(add ? [transaction] : []),
      ];
      return { ...prev, transactionsInfo: newTransactionsInfo };
    });
    if (!add) {
      events.emit('transactionEndUpdated', transaction.transactionId);
    }
  };

  const handleTransactionStart = (transaction: TransactionInfo) => {
    handleTransaction(transaction, true);
  };

  const handleTransactionEnd = (transaction: TransactionInfo) => {
    handleTransaction(transaction, false);
  };

  events.on('transactionStart', handleTransactionStart);
  events.on('transactionEnd', handleTransactionEnd);

  // Don't cleanup transaction listeners on unmount - they should stay active
  // as long as the core worker is running. Code can finish executing while
  // the user is on the dashboard, and we need to track the transaction state.
  // The state is reset via resetEditorInteractionState() when switching files.
  return () => {};
};

const createFieldAtom = <T extends keyof EditorInteractionState>(key: T) =>
  atom(
    (get) => get(editorInteractionStateAtom)[key],
    (get, set, update: SetStateAction<EditorInteractionState[T]>) => {
      const prev = get(editorInteractionStateAtom)[key];
      const newValue =
        typeof update === 'function'
          ? (update as (prev: EditorInteractionState[T]) => EditorInteractionState[T])(prev)
          : update;
      set(editorInteractionStateAtom, { ...get(editorInteractionStateAtom), [key]: newValue });
    }
  );

export const editorInteractionStateShowIsRunningAsyncActionAtom = createFieldAtom('isRunningAsyncAction');
export const editorInteractionStateTransactionsInfoAtom = createFieldAtom('transactionsInfo');
export const editorInteractionStateShowCellTypeMenuAtom = createFieldAtom('showCellTypeMenu');
export const editorInteractionStateShowCommandPaletteAtom = createFieldAtom('showCommandPalette');
export const editorInteractionStateShowAddConnectionMenuAtom = createFieldAtom('showAddConnectionMenu');
export const editorInteractionStateShowConnectionsMenuAtom = createFieldAtom('showConnectionsMenu');
export const editorInteractionStateShowGoToMenuAtom = createFieldAtom('showGoToMenu');
export const editorInteractionStateShowCellEditHistoryAtom = createFieldAtom('showCellEditHistory');
export const editorInteractionStateShowFeedbackMenuAtom = createFieldAtom('showFeedbackMenu');
export const editorInteractionStateShowRenameFileMenuAtom = createFieldAtom('showRenameFileMenu');
export const editorInteractionStateShowShareFileMenuAtom = createFieldAtom('showShareFileMenu');
export const editorInteractionStateShowExportFileMenuAtom = createFieldAtom('showExportFileMenu');
export const editorInteractionStateShowPdfExportMenuAtom = createFieldAtom('showPdfExportMenu');
export const editorInteractionStateShowGoogleSheetsImportAtom = createFieldAtom('showGoogleSheetsImport');
export const editorInteractionStateShowFindReplaceAtom = createFieldAtom('showFindReplace');
export const editorInteractionStateShowContextMenuAtom = createFieldAtom('showContextMenu');
export const editorInteractionStateShowValidationAtom = createFieldAtom('showValidation');
export const editorInteractionStateShowConditionalFormatAtom = createFieldAtom('showConditionalFormat');
export const editorInteractionStateShowCommentsAtom = createFieldAtom('showComments');
export const editorInteractionStateShowChartPanelAtom = createFieldAtom('showChartPanel');
export const editorInteractionStateShowPivotPanelAtom = createFieldAtom('showPivotPanel');
export const editorInteractionStateShowCreatePivotTableAtom = createFieldAtom('showCreatePivotTable');
export const editorInteractionStateShowDefinedNamesAtom = createFieldAtom('showDefinedNames');
export const editorInteractionStateShowLogsAtom = createFieldAtom('showLogs');
export const editorInteractionStateAnnotationStateAtom = createFieldAtom('annotationState');
export const editorInteractionStatePermissionsAtom = createFieldAtom('permissions');
export const editorInteractionStateSettingsAtom = createFieldAtom('settings');
export const editorInteractionStateUserAtom = createFieldAtom('user');
export const editorInteractionStateFileUuidAtom = createFieldAtom('fileUuid');
export const editorInteractionStateTeamUuidAtom = createFieldAtom('teamUuid');
export const editorInteractionStateCanManageBillingAtom = createFieldAtom('canManageBilling');
export const editorInteractionStateCanManageAIOverageAtom = createFieldAtom('canManageAIOverage');
export const editorInteractionStateConnectionUuidAtom = createFieldAtom('connectionUuid');
export const editorInteractionStateFollowAtom = createFieldAtom('follow');
export const editorInteractionStateUndoAtom = createFieldAtom('undo');
export const editorInteractionStateRedoAtom = createFieldAtom('redo');

export const resetEditorInteractionState = () => {
  getDefaultStore().set(editorInteractionStateBaseAtom, defaultEditorInteractionState);
};
